package com.nonopn.skywatcher.common.app.api.impl

import com.nonopn.skywatcher.app.utils.PackageInformation
import com.nonopn.skywatcher.common.api.DoNetworkCalls
import com.nonopn.skywatcher.common.app.api.Mode
import com.nonopn.skywatcher.common.app.pages.map.model.weather.AlertType
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ProbeStatus
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ThunderDetectionManualAlertType
import com.nonopn.skywatcher.common.app.pages.sentinels.ThunderDetectionWithMetadata
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.DefaultClientWebSocketSession
import kotlinx.serialization.Serializable

class ApiAdmin(
    client: HttpClient
) : ApiInterface {
    private val calls = DoNetworkCalls(client)
    private val prefixDetection = "https://api.lpsmanager.io/admin/skysentinel"

    override val mode = Mode.Normal

    override suspend fun profile(): ApiLightUser =
        calls.get("$prefixDetection/user")

    override suspend fun onPushNotificationToken(token: String) {
        calls.post<PushNotificationTokenPost, Unit>(
            "$prefixDetection/user/push", PushNotificationTokenPost(
                token = token,
                app = PackageInformation.name
            )
        )
    }

    override suspend fun status(thunderDetection: ThunderDetectionWithMetadata): ProbeStatus =
        calls.get("$prefixDetection/detection/${thunderDetection.detection.uuid}/status")

    override suspend fun detections(): List<ThunderDetectionWithMetadata> =
        calls.get("$prefixDetection/detections")

    override suspend fun users(thunderDetection: ThunderDetectionWithMetadata): ThunderDetectionUsers =
        calls.get("$prefixDetection/detection/${thunderDetection.detection.uuid}/users")

    override suspend fun registerDetectionUpdate(
        thunderDetection: ThunderDetectionWithMetadata,
        block: suspend DefaultClientWebSocketSession.() -> Unit
    ) = calls.webSocket(
        "$prefixDetection/detection/realtime",
        block
    )

    override suspend fun confirmAlertSeen(thunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSurroundingContext =
        calls.post("$prefixDetection/detection/${thunderDetection.detection.uuid}/alert/confirm")

    override suspend fun sendManualAlert(
        thunderDetection: ThunderDetectionWithMetadata,
        alertType: AlertType
    ): ThunderDetectionSurroundingContext = calls.post(
        "$prefixDetection/detection/${thunderDetection.detection.uuid}/alert/manual",
        ThunderDetectionManualAlert(alertType.toRestApi())
    )

    override suspend fun surroundingUsers(thunderDetection: ThunderDetectionWithMetadata): ThunderDetectionSurroundingContext =
        calls.get("$prefixDetection/detection/${thunderDetection.detection.uuid}/users/surrounding")

    override suspend fun addUser(
        thunderDetection: ThunderDetectionWithMetadata,
        email: String?,
        uuid: String?,
        isAdministrator: Boolean,
        validUntilSeconds: Long?,
    ): ThunderDetectionUsers = calls.post(
        "$prefixDetection/detection/${thunderDetection.detection.uuid}/users",
        ThunderDetectionAddUser(
            email = email,
            uuid = uuid,
            isAdministrator = isAdministrator,
            validUntilSeconds = validUntilSeconds
        )
    )

    override suspend fun removeUser(
        thunderDetection: ThunderDetectionWithMetadata,
        user: ThunderDetectionUser
    ): ThunderDetectionUsers =
        calls.delete("$prefixDetection/detection/${thunderDetection.detection.uuid}/user/${user.uuid}")

    override suspend fun updateLocation(location: ThunderDetectionLocation): ThunderDetectionLocation =
        calls.post("$prefixDetection/gps", location)
}


@Serializable
data class ThunderDetectionManualAlert(
    val type: ThunderDetectionManualAlertType
)